import {
  Box,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Heading,
  Link,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import NiceModal, {useModal} from '@ebay/nice-modal-react';
import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Hit} from 'api-types/ffconnect';
import {State} from '@synako/halitrek';
import {ArticleDesc, DetentionDesc} from 'api-types/ffconnect-hal';
import {DisplayLabels} from '@/domains/panier/components/DisplayLabels';
import {HaliRenderAll, HaliRenderOne, useHalState} from '@synako/haligator';
import {DownloadIcon, InfoIcon} from '@chakra-ui/icons';
import {ImagePrincipaleDuProduit} from '@/domains/mediatheque/ImagePrincipaleDuProduit';
import ColdIcon from '@/components/svg/ColdIcon';
import {ProduitTable} from '@/domains/article/FicheArticle/ProduitTable';
import {ArticleCodesDisplay} from '@/domains/panier/components/ArticleCodesDisplay';
import {useApi} from '@/haligator-factory';
import {toDecimalFr} from 'ff-common';
import {useBlockScroll} from '@/backoffice/hooks/useBlockScroll';
import {getLignesDetentionAvecVente} from './detention';

// Composant de fiche produit séparé pour la partie téléchargement
const FicheProduitSimple: React.FC<{articleState: State<ArticleDesc>}> = ({
  articleState,
}) => {
  const mediasState = useHalState(articleState.follow('medias'));
  const {t} = useTranslation();

  return (
    <VStack
      spacing={2}
      alignItems='stretch'
    >
      <HaliRenderOne
        nav={mediasState.navigable.follow('fiche_produit')}
        render={({state: ficheProduitState}) => (
          <HStack
            bg='white'
            p={4}
            borderRadius='md'
            borderColor='blue.500'
            borderWidth='1px'
            width='100%'
            margin='auto'
            justifyContent='space-between'
          >
            <VStack alignItems='left'>
              <Text
                color='blue.500'
                fontSize='xs'
                fontWeight='700'
              >
                {t('fiche_produit.fiche_produit_pdf.telecharger_fiche_produit')}
              </Text>
            </VStack>
            <Link
              href={ficheProduitState.data.url}
              target='_blank'
              rel='noopener noreferrer'
              boxSize='35px'
              bg='gray.light'
              display='flex'
              alignItems='center'
              justifyContent='center'
              padding={0}
            >
              <DownloadIcon />
            </Link>
          </HStack>
        )}
        notFound={() => (
          <HStack
            bg='gray.75'
            p={4}
            borderRadius='md'
            borderColor='blue.transparency_300_60'
            borderWidth='1px'
            width='100%'
            margin='auto'
            justifyContent='space-between'
          >
            <VStack alignItems='left'>
              <Text
                fontSize='xs'
                color='gray.dark'
                fontWeight='400'
              >
                {t(
                  'fiche_produit.fiche_produit_pdf.fiche_produit_non_disponible',
                )}
              </Text>
            </VStack>
            <Tooltip
              label='Non disponible'
              aria-label='Fiche produit non disponible'
            >
              <Box
                boxSize='35px'
                bg='gray.light'
                display='flex'
                alignItems='center'
                justifyContent='center'
                padding={0}
              >
                <DownloadIcon color='gray.300' />
              </Box>
            </Tooltip>
          </HStack>
        )}
      />
      <ProduitTable
        articleState={articleState}
        mx='0'
        p='0'
      />
    </VStack>
  );
};

// Bloc d'affichage de la détention (titre + tableau)
const DetentionSection: React.FC<{
  detentionState: State<DetentionDesc> | undefined;
  backoffice: boolean;
}> = ({detentionState, backoffice}) => {
  const {t} = useTranslation();
  const api = useApi();

  // Ne rien afficher si non backoffice ou données absentes
  if (!backoffice) return null;
  if (!detentionState)
    return (
      <VStack alignItems='stretch'>
        <Skeleton height='50px' />
        <Skeleton height='100px' />
      </VStack>
    );

  const tableHeight = 220;
  const lineHeight = tableHeight / 5;

  const lignes = getLignesDetentionAvecVente(
    detentionState.data.tolede.commercialise_par,
  );

  // Crée les liens pour charger toutes les sociétés commerciales en une seule fois
  const societeLinks = lignes.map((ligne) =>
    api.follow('get-cache-societe_commerciale', {
      code_societe_commerciale: ligne.code_societe_commerciale,
    }),
  );

  return (
    <VStack alignItems='left'>
      <Text
        fontSize='xl'
        fontWeight='bold'
        mb={4}
      >
        {t('grand_compte.detention_section.title', {count: lignes.length})}
      </Text>

      {/* Tableau à deux colonnes, header gris, lignes zébrées, max 4 lignes visibles */}
      <TableContainer
        maxH={tableHeight}
        overflowY='auto'
        overflowX='hidden'
        borderRadius='8px'
        w='100%'
      >
        <Table
          variant='unstyled'
          size='sm'
          width='100%'
        >
          <Thead
            bg='gray.dark'
            borderTopRadius='8px'
            position='sticky'
            top='0'
          >
            <Tr
              color='white.100'
              h={`${lineHeight}px`}
            >
              <Th
                color='white.100'
                textTransform='none'
                textAlign='left'
                fontSize={{base: '2xs', md: 'xs'}}
                p={2}
                px={{base: 2, md: 4}}
                width='70%'
              >
                {t('grand_compte.detention_section.societe_commerciale')}
              </Th>
              <Th
                color='white.100'
                textTransform='none'
                textAlign='center'
                fontSize={{base: '2xs', md: 'xs'}}
                p={2}
                px={{base: 2, md: 4}}
                width='30%'
              >
                <Tooltip
                  label={t(
                    'grand_compte.detention_section.vendu_3_derniers_mois_tooltip',
                  )}
                  aria-label={t(
                    'grand_compte.detention_section.vendu_3_derniers_mois_tooltip',
                  )}
                >
                  {t('grand_compte.detention_section.vendu_3_derniers_mois')}
                  <InfoIcon
                    boxSize='15px'
                    ml='2'
                  />
                </Tooltip>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {lignes.length === 0 ? (
              // État vide : affiche un message sur toute la largeur
              <Tr>
                <Td
                  colSpan={2}
                  p={4}
                  textAlign='center'
                  fontSize={{base: 'xs', md: 'sm'}}
                  color='gray.dark'
                  bg='gray.light'
                  fontStyle='italic'
                >
                  {t('grand_compte.detention_section.aucune_societe_vendu')}
                </Td>
              </Tr>
            ) : (
              // Charge toutes les sociétés commerciales puis trie par ordre alphabétique
              <HaliRenderAll
                all={societeLinks}
                render={({states: societeStates}) => {
                  // Combine les données de détention avec les noms de société
                  const lignesAvecNoms = lignes.map((ligne, idx) => ({
                    ...ligne,
                    nomCommercial:
                      societeStates[idx]?.data.nom_commercial ?? '',
                  }));

                  // Tri alphabétique par nom commercial (insensible à la casse)
                  const lignesTries = lignesAvecNoms.sort((a, b) =>
                    a.nomCommercial.localeCompare(b.nomCommercial, 'fr', {
                      sensitivity: 'base',
                    }),
                  );

                  return (
                    <>
                      {lignesTries.map((ligne, index) => {
                        const vendu = ligne.poids_3_derniers_mois;
                        return (
                          <Tr
                            key={`${ligne.code_societe_commerciale}-${index}`}
                            fontSize={{base: 'xs', md: 'sm'}}
                            bg={index % 2 === 0 ? 'white' : 'gray.light'}
                            h={`${lineHeight}px`}
                          >
                            <Td
                              p={2}
                              px={{base: 2, md: 4}}
                              textAlign='left'
                              width='70%'
                              whiteSpace='normal'
                              wordBreak='break-word'
                            >
                              <Text>{ligne.nomCommercial}</Text>
                            </Td>
                            <Td
                              p={2}
                              px={{base: 2, md: 4}}
                              textAlign='center'
                              width='30%'
                            >
                              {/* Affiche la quantité + unité de facturation */}
                              {toDecimalFr(vendu, 0, 2)}&nbsp;
                              {t(`unite.KG`, {count: vendu})}
                            </Td>
                          </Tr>
                        );
                      })}
                    </>
                  );
                }}
              />
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </VStack>
  );
};

const ReferenceSection: React.FC<{referencePar: string | undefined}> = ({
  referencePar,
}) => {
  const {t} = useTranslation();
  if (!referencePar) return null;
  return (
    <HStack
      alignItems='end'
      my='20px'
      spacing='1'
    >
      <Text fontWeight='700'>{t('grand_compte.reference')}:</Text>
      <Text>{t(`grand_compte.reference_par.${referencePar}`)}</Text>
    </HStack>
  );
};

// Composant principal exporté
export const ModaleDetailsProduitCatalogue = NiceModal.create<{
  hit: Hit;
  articleState?: State<ArticleDesc>;
  backoffice: boolean;
}>(({hit, articleState, backoffice}) => {
  const modal = useModal();
  const {t} = useTranslation();
  const detentionHook = useHalState(articleState?.follow('detention'));
  const detentionState = detentionHook.state;

  // Le titre à utiliser dans la modale (code article et libellé)
  const code_article =
    articleState?.data.tolede.code_article ?? hit.code_article;
  const libelle =
    articleState?.data.tolede.libelle_commercial ??
    articleState?.data.tolede.libelle ??
    hit.libelle_commercial;
  const marque = articleState?.data.tolede.marque?.[0] ?? '';
  const lePlusProduit = articleState?.data.tolede.le_plus_produit;

  useBlockScroll(modal.visible);

  return (
    <Drawer
      placement='right'
      isOpen={modal.visible}
      onClose={() => {
        modal.remove();
      }}
      size='lg'
    >
      <DrawerOverlay />
      <DrawerContent
        position='relative'
        bg='#F5F5F5'
        transition='all 0.2s ease-out'
      >
        <VStack
          height='100%'
          spacing={0}
        >
          {/* En-tête bleue avec le titre et le bouton de fermeture */}
          <HStack
            p='4'
            bg='blue.main'
            color='white'
            position='sticky'
            top='0'
            zIndex='sticky'
            w='100%'
            spacing={4}
          >
            <VStack
              flex={1}
              alignItems='flex-start'
            >
              <Text>
                <Trans t={t}>{t('global.fiche_produit')}</Trans>
              </Text>
            </VStack>
            <Box>
              <DrawerCloseButton position='static' />
            </Box>
          </HStack>

          {/* Contenu de la modale - Adaptation du ProduitResume */}
          <Box
            flex={1}
            w='100%'
            overflowY='auto'
          >
            <VStack
              m='20px'
              alignItems='left'
              mb='50px'
              p={{base: '10px 20px 0px 20px', md: ''}}
            >
              <ArticleCodesDisplay code_article={code_article} />
              <Heading
                as='h4'
                size={{base: 'sm', md: 'md'}}
              >
                {libelle}
              </Heading>
              <Text size={{base: 'sm', md: 'md'}}>{marque}</Text>
              <Box>
                {articleState && (
                  <Box position={'relative'}>
                    <ImagePrincipaleDuProduit articleState={articleState} />
                    <Box
                      position='absolute'
                      bottom={-3}
                      left={'20px'}
                    >
                      {articleState.data.temperature_livraison ===
                        'negatif' && <ColdIcon boxSize='54px' />}
                    </Box>
                  </Box>
                )}
              </Box>
              <HStack>
                {articleState && <DisplayLabels articleState={articleState} />}
              </HStack>

              {/* Détention */}
              <DetentionSection
                detentionState={detentionState}
                backoffice={backoffice}
              />

              {lePlusProduit && (
                <>
                  {/* Titre */}
                  <Text
                    fontSize='md'
                    color='gray.dark'
                    fontWeight='600'
                    mt='20px'
                  >
                    {t('fiche_produit.description')}
                  </Text>
                  {/* Contenu */}
                  <Text
                    fontSize='sm'
                    color='gray.dark'
                    fontWeight='400'
                    lineHeight='20px'
                  >
                    {lePlusProduit}
                  </Text>
                </>
              )}

              {/* Section référence par national/filiale */}
              <ReferenceSection
                referencePar={articleState?.data.tolede.reference_par}
              />

              {/* Section fiche produit */}
              {articleState && (
                <FicheProduitSimple articleState={articleState} />
              )}
            </VStack>
          </Box>
        </VStack>
      </DrawerContent>
    </Drawer>
  );
});
